import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { ArrowRight, ExternalLink, Radio } from 'lucide-react'
import type { NewsItem } from '../../types/news'
import { isFeaturedNewsItem, isPublisherSponsored, newsItemTickers } from '../../services/news/presentation'
import { ArticleMetadata } from './ArticleMetadata'
import { ArticleSignals } from './ArticleSignals'
import { getNewsCategoryConfig } from './newsCategoryConfig'
import { NewsCardMedia } from './NewsCardMedia'
import { trackReaderEvent } from '../../services/analytics'
import { IndustryEffectTags } from './IndustryEffectTags'
import { StoryActionBar } from '../StoryActionBar/StoryActionBar'
import { StoryClassificationMenu } from '../StoryActionBar/StoryClassificationMenu'
import { markStoryOpened } from '../../services/reader'
import { NewsSourceTypeBadge } from './NewsExperience'
import { getNewsSourcePresentation } from './newsCuration'
import { reviewDisclosure } from '../../../functions/src/reviewDisclosure'

export function NewsStoryCard({ item, featured = isFeaturedNewsItem(item), coveragePublisherCount = 0, coverageClusterId }: { item: NewsItem; featured?: boolean; coveragePublisherCount?: number; coverageClusterId?: string }) {
  const config = getNewsCategoryConfig(item.categories[0])
  const tickers = useMemo(() => newsItemTickers(item), [item])
  const isAiArticle = item.kind === 'ai-synthesis'
  const editorialReviewLabel = reviewDisclosure(item).label.toUpperCase()
  const publisherSponsored = isPublisherSponsored(item)
  const sourcePresentation = getNewsSourcePresentation(item)
  const translationLabel = item.originalLanguage ? `TRANSLATED FROM ${item.originalLanguage.toUpperCase()} · ` : ''
  const evidenceStatus = isAiArticle
    ? `OMIKINA ANALYSIS · SOURCE-LINKED · ${editorialReviewLabel}`
    : publisherSponsored
      ? 'PUBLISHER-SPONSORED SOURCE · OPEN TO VERIFY'
      : sourcePresentation.type === 'preprint'
        ? 'RESEARCH DISCOVERY · REVIEW STATUS UNVERIFIED'
        : sourcePresentation.type === 'vendor'
          ? 'FIRST-PARTY CLAIMS · VERIFY INDEPENDENTLY'
          : sourcePresentation.type === 'official'
            ? 'PRIMARY PUBLICATION · ADD INDEPENDENT CONTEXT'
            : `${translationLabel}DISCOVERY SIGNAL · OPEN SOURCE TO VERIFY`
  const isSharedStory = new URLSearchParams(window.location.search).get('story') === item.id
  const shareTracking = { utm_source: 'reader_share', utm_medium: 'organic_social', legacy_event_name: 'story_share' } as const
  const card = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!card.current || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { trackReaderEvent('story_impression', { story_id: item.id, source_id: item.sourceId }); observer.disconnect() } }, { threshold: .5 })
    observer.observe(card.current); return () => observer.disconnect()
  }, [item.id, item.sourceId])

  return <article
    ref={card}
    className={`news-card news-card--${config.slug} ${featured ? 'is-featured' : ''} ${publisherSponsored ? 'is-publisher-sponsored' : ''}`}
    style={{ '--news-tone': config.color } as CSSProperties}
    data-featured={featured || undefined}
    data-story-id={item.id}
    data-shared={isSharedStory || undefined}
    data-source-type={sourcePresentation.type}
  >
    <a className="news-card-link" href={item.url} target={isAiArticle ? undefined : '_blank'} rel={isAiArticle ? undefined : 'noreferrer'} aria-label={`${item.title} — ${item.publisher}${isAiArticle ? '' : ' (opens in a new tab)'}`} onClick={() => { markStoryOpened(item.id); trackReaderEvent(isAiArticle ? 'story_open' : 'source_open', { source_id: item.sourceId, category: item.categories[0], story_id: item.id, placement: 'story_card' }) }}>
      <NewsCardMedia item={item} featured={featured}/>
      <div className="news-card-body">
        <div className="news-card-source-row"><NewsSourceTypeBadge item={item}/><span className="news-card-evidence-status">{evidenceStatus}</span></div>
        <ArticleMetadata publisher={item.publisher} publishedAt={item.publishedAt}/>
        <ArticleSignals categories={item.categories.slice(0, 1)} tickers={[]}/>
        <h2>{item.title}{isAiArticle ? <ArrowRight size={15} aria-hidden="true"/> : <ExternalLink size={15} aria-hidden="true"/>}</h2>
        {item.summary && <p className="news-card-summary">{item.summary}</p>}
        {coveragePublisherCount > 1 && <span className="news-card-coverage"><Radio size={12} aria-hidden="true"/> ALSO REPORTED BY {coveragePublisherCount - 1} OTHER {coveragePublisherCount === 2 ? 'PUBLISHER' : 'PUBLISHERS'}</span>}
        <IndustryEffectTags industries={item.industries}/>
        <div className="news-card-footer">
          <span className="news-card-read-source">{isAiArticle ? 'READ OMIKINA UPDATE' : 'READ SOURCE'} <ArrowRight size={14} aria-hidden="true"/></span>
        </div>
      </div>
    </a>
    {(item.categories.length > 1 || tickers.length > 0 || item.classification) && <details className="news-card-context"><summary>More context</summary><ArticleSignals categories={item.categories.slice(1)} tickers={tickers}/>{item.classification && <p>{item.classification.classificationReason}</p>}</details>}
    <StoryActionBar item={item} compareHref={coveragePublisherCount > 1 && coverageClusterId ? `/story/${coverageClusterId}` : undefined} shareTracking={shareTracking}/>
    <StoryClassificationMenu item={item}/>
  </article>
}
